import { useEffect, useMemo } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography,
    Checkbox,
} from "@material-tailwind/react";
import { sortThemesByName } from "../utils/sort-themes";

function RegisterCheckbox({ name, label, ...props }) {
    const { register } = useFormContext();
    const { ref, ...rest } = register(name);
    return (
        <Checkbox
            label={label}
            {...rest}
            {...props}
            inputRef={ref}
        />
    );
}

export default function RemoveFromThemeDialog({
    open,
    onClose,
    themes = [], // [{ id, name }]
    responses = [],
    onRemoveFromTheme, // (selectedThemeIds, responses) => {}
}) {
    const handleClose = () => {
        onClose();
    };
    const sortedThemes = useMemo(() => sortThemesByName(themes), [themes]);
    const methods = useForm({
        mode: "onChange",
        defaultValues: sortedThemes.reduce(
            (acc, theme) => ({ ...acc, [theme.id]: false }),
            {},
        ),
    });
    // Reset once per open transition; the reduce builds a fresh object
    // each render, so it stays out of the dependency list on purpose.
    const { reset } = methods;
    useEffect(() => {
        if (open) {
            reset(sortedThemes.reduce(
                (acc, theme) => ({ ...acc, [theme.id]: false }),
                {},
            ));
        }
    }, [open, reset]);
    const watchedValues = methods.watch();

    const handleSubmit = (values) => {
        const selectedThemeIds = Object.entries(values)
            .filter(([_, checked]) => checked === true)
            .map(([id]) => id);

        if (selectedThemeIds.length > 0) {
            onRemoveFromTheme(selectedThemeIds, responses);
            onClose();
        }
    };

    return (
        <Dialog open={open} handler={handleClose} className="dialog">
            <DialogHeader className="dialog__header">
                <Typography variant="h4">Remove from Theme</Typography>
            </DialogHeader>

            <FormProvider {...methods}>
                <form
                    onSubmit={(e) => {
                        // Portaled dialog forms keep their React-tree
                        // position, so a submit here bubbles into any host
                        // form (the survey builder's form) and
                        // double-submits. Stop it at the source.
                        e.stopPropagation();
                        return methods.handleSubmit(handleSubmit)(e);
                    }}
                >
                    <DialogBody className="dialog__body">
                        <div className="dialog__section max-h-64 overflow-y-auto space-y-2">
                            {sortedThemes.map((theme) => (
                                <div
                                    key={theme.id}
                                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md"
                                >
                                    <RegisterCheckbox
                                        name={theme.id.toString()}
                                        label={theme.name}
                                        className="text-primary font-body"
                                        labelProps={{
                                            className:
                                                "text-primary font-body",
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </DialogBody>

                    <DialogFooter className="dialog__footer">
                        <Button
                            type="button"
                            className="btn btn--danger"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="btn btn--primary"
                            disabled={!Object.values(watchedValues).some(
                                (v) => v,
                            )}
                        >
                            Remove from Theme
                        </Button>
                    </DialogFooter>
                </form>
            </FormProvider>
        </Dialog>
    );
}
